import type { ValueOf } from "../utils/types";
import type { DbEnv } from "./db";

export const HIGHARC_DOMAIN = "higharc.com";

export const ENVIRONMENTS: Record<EnvironmentKey | "master", EnvironmentKey> = {
  docker_test: "docker_test",
  local: "local",
  local_test: "local_test",
  sandbox1: "sandbox1",
  sandbox2: "sandbox2",
  sandbox3: "sandbox3",
  sandbox4: "sandbox4",
  sandbox5: "sandbox5",
  sandbox6: "sandbox6",
  sandbox7: "sandbox7",
  sandbox8: "sandbox8",
  sandbox9: "sandbox9",
  sandbox10: "sandbox10",
  sandbox11: "sandbox11",
  sandbox12: "sandbox12",
  sandbox13: "sandbox13",
  sandbox14: "sandbox14",
  sandbox15: "sandbox15",
  sandbox16: "sandbox16",
  sandbox17: "sandbox17",
  sandbox18: "sandbox18",
  sandbox19: "sandbox19",
  sandbox20: "sandbox20",
  demo1: "demo1",
  demo2: "demo2",
  pr: "pr",
  develop: "develop",
  qa: "qa",
  stage: "stage",
  prod: "prod",
  master: "prod", // In the event Git branch is mistakenly used to determine URL
} as const;

export const BASE_URLS: Record<EnvironmentKey | "master", string> = {
  docker_test: "http://localhost:3000",
  local: "http://localhost:3000",
  local_test: "http://localhost:3000",
  sandbox1: "https://app.sandbox1.higharc.com",
  sandbox2: "https://app.sandbox2.higharc.com",
  sandbox3: "https://app.sandbox3.higharc.com",
  sandbox4: "https://app.sandbox4.higharc.com",
  sandbox5: "https://app.sandbox5.higharc.com",
  sandbox6: "https://app.sandbox6.higharc.com",
  sandbox7: "https://app.sandbox7.higharc.com",
  sandbox8: "https://app.sandbox8.higharc.com",
  sandbox9: "https://app.sandbox9.higharc.com",
  sandbox10: "https://app.sandbox10.higharc.com",
  sandbox11: "https://app.sandbox11.higharc.com",
  sandbox12: "https://app.sandbox12.higharc.com",
  sandbox13: "https://app.sandbox13.higharc.com",
  sandbox14: "https://app.sandbox14.higharc.com",
  sandbox15: "https://app.sandbox15.higharc.com",
  sandbox16: "https://app.sandbox16.higharc.com",
  sandbox17: "https://app.sandbox17.higharc.com",
  sandbox18: "https://app.sandbox18.higharc.com",
  sandbox19: "https://app.sandbox19.higharc.com",
  sandbox20: "https://app.sandbox20.higharc.com",
  demo1: "https://app.demo1.higharc.com",
  demo2: "https://app.demo2.higharc.com",
  pr: process.env.PUBLIC_BASE_URL ?? "https://pr.higharc.com",
  develop: "https://app.dev.higharc.com",
  qa: "https://app.qa.higharc.com",
  stage: "https://app.stage.higharc.com",
  prod: `https://app.${HIGHARC_DOMAIN}`,
  master: `https://app.${HIGHARC_DOMAIN}`, // In the event Git branch is used to determine URL
} as const;

/**
 * The set of externally-reachable Higharc app origins across every deployment (all `BASE_URLS`
 * entries except localhost). Used to validate that a URL targets one of our own deployments — the
 * client may build a URL against a different environment's origin than the server it talks to (e.g.
 * a sandbox browser resolves `publicAppBaseUrl` to develop, see `resolveRuntimeEnvFromHostname`), so
 * an exact match against the current env's origin is too strict.
 */
const KNOWN_APP_ORIGINS: ReadonlySet<string> = new Set(
  Object.values(BASE_URLS)
    .filter((url) => !url.includes("localhost"))
    .map((url) => new URL(url).origin),
);

/** Whether `origin` is one of our own public app origins across any deployment. */
export function isKnownAppOrigin(origin: string): boolean {
  return KNOWN_APP_ORIGINS.has(origin);
}

/**
 * Environments that have their own Cognito user pool. This governs *pool selection* only, not env
 * identity: `resolveRuntimeEnvFromHostname` resolves the true env for every host (sandboxes, PR,
 * etc.), while envs not listed here authenticate against develop's pool (see `cognitoConfigForEnv`).
 */
export type RuntimeEnv = "prod" | "stage" | "qa" | "demo1" | "demo2" | "develop";

export interface CognitoConfig {
  clientId: string;
  userPoolId: string;
  userPoolDomain: string;
}

/**
 * Public Cognito identifiers per environment. These are not secret — the browser bundle
 * has always shipped them — so the SPA can carry all of them and pick one at runtime.
 */
export const COGNITO_BY_ENV: Record<RuntimeEnv, CognitoConfig> = {
  prod: {
    clientId: "6enor0c2skvi41hg1e18i87ao3",
    userPoolId: "us-east-1_qV0LIyzpf",
    userPoolDomain: "https://higharc-sso-prod.auth.us-east-1.amazoncognito.com",
  },
  stage: {
    clientId: "5sra8m53h0jck7rmmoiaoc0617",
    userPoolId: "us-east-1_vY9B4JSNO",
    userPoolDomain: "https://higharc-sso-stage.auth.us-east-1.amazoncognito.com",
  },
  qa: {
    clientId: "3b92mb3lioqbo39fm90j058laa",
    userPoolId: "us-east-1_odJieO4lr",
    userPoolDomain: "https://higharc-qa.auth.us-east-1.amazoncognito.com",
  },
  demo1: {
    clientId: "6r9ur26rromnc2dlu2h18ftvrp",
    userPoolId: "us-east-1_qfoWbtCR7",
    userPoolDomain: "https://higharc-demo1.auth.us-east-1.amazoncognito.com",
  },
  demo2: {
    clientId: "5lkihj4r7gskso38fkuuai6vje",
    userPoolId: "us-east-1_ROrnoIM9i",
    userPoolDomain: "https://higharc-demo2.auth.us-east-1.amazoncognito.com",
  },
  develop: {
    clientId: "vnun7a005irajdgmqujvt4j0n",
    userPoolId: "us-east-1_wRprpHnwb",
    userPoolDomain: "https://higharc-sso-develop.auth.us-east-1.amazoncognito.com",
  },
};

function isRuntimeEnv(env: string): env is RuntimeEnv {
  return Object.prototype.hasOwnProperty.call(COGNITO_BY_ENV, env);
}

/**
 * The Cognito config for an environment. Envs without their own pool (PR, local, sandboxes)
 * authenticate against develop's.
 */
export function cognitoConfigForEnv(env: string | undefined): CognitoConfig {
  return env !== undefined && isRuntimeEnv(env) ? COGNITO_BY_ENV[env] : COGNITO_BY_ENV.develop;
}

/**
 * Each runtime env is normally reached through its vanity domain (BASE_URLS), but its raw
 * CloudFront distribution domain is also a valid entry point (e.g. before DNS is wired, or
 * bypassing the vanity host), so map those too. Values are the `distribution_domain_name`
 * terraform output per env; unlike vanity domains they change if a distribution is recreated, so
 * refresh them if that happens. Note this resolves env identity only — hosted-UI SSO on these
 * hosts additionally requires the CloudFront callback URL registered in the Cognito app client.
 */
const CLOUDFRONT_HOSTNAME_BY_ENV: Record<RuntimeEnv, string> = {
  prod: "d1prj2dvashjeb.cloudfront.net",
  stage: "d2hxvnfvqebpzb.cloudfront.net",
  qa: "d3fv76ze594pg8.cloudfront.net",
  demo1: "d15uzo2499ap9y.cloudfront.net",
  demo2: "d1g6zhrrkx0ng0.cloudfront.net",
  develop: "d1hq9l7lp7tghn.cloudfront.net",
};

// Every env reachable by a stable hostname maps to its own identity, including sandboxes and the
// demo envs, not just the ones with a Cognito pool. localhost/test and the PR wildcard are handled
// specially in the resolver. Both the vanity domain (BASE_URLS) and, where known, the raw
// CloudFront domain resolve to the env.
const HOSTNAME_UNROUTABLE = new Set<string>(["docker_test", "local", "local_test", "pr", "master"]);

const RUNTIME_ENV_BY_HOSTNAME: Record<string, EnvironmentKey> = Object.keys(BASE_URLS).reduce<
  Record<string, EnvironmentKey>
>((acc, env) => {
  if (HOSTNAME_UNROUTABLE.has(env)) {
    return acc;
  }
  acc[new URL(BASE_URLS[env as EnvironmentKey]).hostname] = env as EnvironmentKey;
  return acc;
}, {});
for (const [env, hostname] of Object.entries(CLOUDFRONT_HOSTNAME_BY_ENV)) {
  RUNTIME_ENV_BY_HOSTNAME[hostname] = env as EnvironmentKey;
}

/**
 * Resolve the deploy environment from the hostname the app is served from, so a single built
 * artifact behaves correctly in any environment. `localhost`/`127.0.0.1` resolve to `local` and
 * PR hosts (`<n>.pr.higharc.com`) to `pr`, distinct identities so they aren't treated as a real
 * remote deployment for analytics/RUM. Every other known host resolves to its actual env (sandboxes
 * included); genuinely unknown hosts fall back to develop. This is env *identity*; Cognito pool
 * selection is separate (`cognitoConfigForEnv`), so e.g. sandboxes resolve to their own env here but
 * still authenticate against develop's pool.
 */
export function resolveRuntimeEnvFromHostname(hostname: string): EnvironmentKey {
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "local";
  }
  if (hostname.endsWith(".pr.higharc.com")) {
    return "pr";
  }
  return RUNTIME_ENV_BY_HOSTNAME[hostname] ?? "develop";
}

/**
 * Branches that correspond to a deployment.
 */
export type ReleaseBranch =
  | "sandbox1"
  | "sandbox2"
  | "sandbox3"
  | "sandbox4"
  | "sandbox5"
  | "sandbox6"
  | "sandbox7"
  | "sandbox8"
  | "sandbox9"
  | "sandbox10"
  | "sandbox11"
  | "sandbox12"
  | "sandbox13"
  | "sandbox14"
  | "sandbox15"
  | "sandbox16"
  | "sandbox17"
  | "sandbox18"
  | "sandbox19"
  | "sandbox20"
  | "demo1"
  | "demo2"
  | "develop"
  | "qa"
  | "stage"
  | "master";

export type ReleaseBranchPrefix = "rc-";
export const RELEASE_BRANCH_PREFIX: ReleaseBranchPrefix = "rc-";

/**
 * These map to our Terraform environments.
 */
export const ENV_LIST = [
  "docker_test",
  "local",
  "local_test",
  "sandbox1",
  "sandbox2",
  "sandbox3",
  "sandbox4",
  "sandbox5",
  "sandbox6",
  "sandbox7",
  "sandbox8",
  "sandbox9",
  "sandbox10",
  "sandbox11",
  "sandbox12",
  "sandbox13",
  "sandbox14",
  "sandbox15",
  "sandbox16",
  "sandbox17",
  "sandbox18",
  "sandbox19",
  "sandbox20",
  "demo1",
  "demo2",
  "pr",
  "develop",
  "qa",
  "stage",
  "prod",
] as const;
export type EnvironmentKey = (typeof ENV_LIST)[number];

export const CONTENT_BASE_URLS: Record<EnvironmentKey | "master", string> = {
  docker_test: "https://content.localdev.higharc.com",
  local: "https://content.localdev.higharc.com",
  local_test: "https://content.localdev.higharc.com",
  sandbox1: "https://content.sandbox1.higharc.com",
  sandbox2: "https://content.sandbox2.higharc.com",
  sandbox3: "https://content.sandbox3.higharc.com",
  sandbox4: "https://content.sandbox4.higharc.com",
  sandbox5: "https://content.sandbox5.higharc.com",
  sandbox6: "https://content.sandbox6.higharc.com",
  sandbox7: "https://content.sandbox7.higharc.com",
  sandbox8: "https://content.sandbox8.higharc.com",
  sandbox9: "https://content.sandbox9.higharc.com",
  sandbox10: "https://content.sandbox10.higharc.com",
  sandbox11: "https://content.sandbox11.higharc.com",
  sandbox12: "https://content.sandbox12.higharc.com",
  sandbox13: "https://content.sandbox13.higharc.com",
  sandbox14: "https://content.sandbox14.higharc.com",
  sandbox15: "https://content.sandbox15.higharc.com",
  sandbox16: "https://content.sandbox16.higharc.com",
  sandbox17: "https://content.sandbox17.higharc.com",
  sandbox18: "https://content.sandbox18.higharc.com",
  sandbox19: "https://content.sandbox19.higharc.com",
  sandbox20: "https://content.sandbox20.higharc.com",
  demo1: "https://content.demo1.higharc.com",
  demo2: "https://content.demo2.higharc.com",
  pr: process.env.PUBLIC_CONTENT_BASE_URL ?? "https://content.pr.higharc.com",
  develop: "https://content.dev.higharc.com",
  qa: "https://content.qa.higharc.com",
  stage: "https://content.stage.higharc.com",
  prod: `https://content.${HIGHARC_DOMAIN}`,
  master: `https://content.${HIGHARC_DOMAIN}`, // In the event Git branch is used to determine URL
} as const;

/** Describes a Cloudflare worker's client-facing URL for use with `workerUrls`. */
export interface WorkerUrlSpec {
  /** wrangler worker name, e.g. "collab-worker". */
  name: string;
  /** "wss" for WebSocket workers, "https" for request/response workers. */
  protocol: "wss" | "https";
  /** Port `wrangler dev` serves locally (see the CF worker registry in tools/worker). */
  localPort: number;
  /** Whether PR previews use an environment-shared `-pr` deployment. */
  sharedPrDeployment?: boolean;
}

/**
 * Client-facing URL for one worker in one environment.
 *
 * Workers that opt into `sharedPrDeployment` resolve `pr` to their `-pr` worker.
 */
export function workerUrl(spec: WorkerUrlSpec, env: EnvironmentKey | "master"): string {
  if (env === "local" || env === "local_test" || env === "docker_test") {
    return `${spec.protocol}://localhost:${spec.localPort}`;
  }
  const suffix = getWorkerEnvSuffix(spec, env);
  return `${spec.protocol}://${spec.name}${suffix}.higharc.workers.dev`;
}

function getWorkerEnvSuffix(spec: WorkerUrlSpec, env: EnvironmentKey | "master"): string {
  if (env === "develop") {
    return "";
  }
  if (env === "pr") {
    return spec.sharedPrDeployment ? "-pr" : "";
  }
  if (env === "prod" || env === "master") {
    return "-production";
  }
  return `-${env}`;
}

export function workerUrls(spec: WorkerUrlSpec): Record<EnvironmentKey | "master", string> {
  const entries = [...ENV_LIST, "master" as const].map((env) => [env, workerUrl(spec, env)] as const);
  return Object.fromEntries(entries) as Record<EnvironmentKey | "master", string>;
}

/** Returns null outside local dev; in local dev returns the start command to show. */
export function workerNotRunningMessage(workerName: string, env: EnvironmentKey | "master"): string | null {
  if (env !== "local" && env !== "local_test" && env !== "docker_test") {
    return null;
  }
  return `The "${workerName}" Cloudflare worker is not running. Start it with: yarn higharc dev app --offline --cf ${workerName}`;
}

/** Collab (Studio DO editing) worker. Shared by the CF worker registry and the client URL map. */
export const COLLAB_WORKER_SPEC: WorkerUrlSpec = {
  name: "collab-worker",
  protocol: "wss",
  localPort: 8787,
  sharedPrDeployment: true,
};

export const COLLAB_WORKER_URLS = workerUrls(COLLAB_WORKER_SPEC);

export const AGENT_WORKER_SPEC: WorkerUrlSpec = {
  name: "agent-worker",
  protocol: "https",
  localPort: 8788,
};

export const AGENT_WORKER_URLS = workerUrls(AGENT_WORKER_SPEC);

export const DB_ENV_URL_MAP = {
  // db dumps
  develop_local_dump: BASE_URLS.local,
  stage_local_dump: BASE_URLS.local,
  prod_local_dump: BASE_URLS.local,

  // tests
  local_test: BASE_URLS.local_test,
  docker_test: BASE_URLS.docker_test,

  // app url
  local: BASE_URLS.local,
  sandbox1: BASE_URLS.sandbox1,
  sandbox2: BASE_URLS.sandbox2,
  sandbox3: BASE_URLS.sandbox3,
  sandbox4: BASE_URLS.sandbox4,
  sandbox5: BASE_URLS.sandbox5,
  sandbox6: BASE_URLS.sandbox6,
  sandbox7: BASE_URLS.sandbox7,
  sandbox8: BASE_URLS.sandbox8,
  sandbox9: BASE_URLS.sandbox9,
  sandbox10: BASE_URLS.sandbox10,
  sandbox11: BASE_URLS.sandbox11,
  sandbox12: BASE_URLS.sandbox12,
  sandbox13: BASE_URLS.sandbox13,
  sandbox14: BASE_URLS.sandbox14,
  sandbox15: BASE_URLS.sandbox15,
  sandbox16: BASE_URLS.sandbox16,
  sandbox17: BASE_URLS.sandbox17,
  sandbox18: BASE_URLS.sandbox18,
  sandbox19: BASE_URLS.sandbox19,
  sandbox20: BASE_URLS.sandbox20,
  demo1: BASE_URLS.demo1,
  demo2: BASE_URLS.demo2,
  pr: BASE_URLS.pr,
  develop: BASE_URLS.develop,
  qa: BASE_URLS.qa,
  stage: BASE_URLS.stage,
  prod: BASE_URLS.prod,
} as const;

export type Server = ValueOf<typeof ENVIRONMENTS>;

export interface ServerEnvironment {
  server: Server;
  cognito: Server;
  db: DbEnv;
  localhost: boolean;
  branch: string;
  tag: string;
  commit: string;
  developmentMode: boolean;
}

export const isServer = typeof window === "undefined";

export const TEST_E2E_MODE = process.env.TEST_E2E_MODE === "true";
